import React from 'react';
import { Box } from '@mui/material';
import { Button, CircularProgress } from '@mui/material';
import { Alert } from 'c-components/MuiOverrides';
import { IfHasAllPermissions } from 'c-auth-module/Components';
import type { CampaignError } from 'c-sdk';
import { PermissionName } from 'c-sdk';
import CampaignErrorAlert from 'c-main/Components/Campaign/CampaignError/CampaignErrorAlert';

type Props = {
    hasInvalidCreatives: boolean;
    onFilterInvalid: () => void;
    invalidCreativesLabel: React.ReactNode;
    filterAllButtonLabel: React.ReactNode;
    errors: CampaignError[];
    hideOutcome?: boolean;
    isRevalidating?: boolean;
    onRevalidate?: () => void;
    showRevalidate?: boolean;
    revalidateLabel?: React.ReactNode;
    revalidateNotice?: React.ReactNode;
};

const perms = [PermissionName.UniledportalCampaign_errorRead];

const CreativeIssuesGrid: React.FC<Props> = ({
    hasInvalidCreatives,
    onFilterInvalid,
    invalidCreativesLabel,
    filterAllButtonLabel,

    errors,
    hideOutcome = false,

    showRevalidate = false,
    isRevalidating = false,
    onRevalidate,
    revalidateLabel,
    revalidateNotice,
}) => {
    const showAnything = showRevalidate || hasInvalidCreatives || (errors && errors.length > 0);

    if (!showAnything) return null;

    return (
        <Box
            sx={{
                width: '100%',
                mb: 2,
                display: 'grid',
                gap: 1.5,
                gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(auto-fit, minmax(260px, 1fr))',
                },
                alignItems: 'stretch',
            }}
        >
            {showRevalidate && (
                <Alert
                    severity="warning"
                    variant="outlined"
                    action={
                        <Button
                            variant="text"
                            color="inherit"
                            disabled={isRevalidating}
                            onClick={onRevalidate}
                        >
                            {isRevalidating ? <CircularProgress size={15} /> : revalidateLabel}
                        </Button>
                    }
                >
                    {revalidateNotice}
                </Alert>
            )}

            {hasInvalidCreatives && (
                <Alert
                    severity="warning"
                    variant="outlined"
                    action={
                        <Button variant="text" color="inherit" onClick={onFilterInvalid}>
                            {filterAllButtonLabel}
                        </Button>
                    }
                >
                    {invalidCreativesLabel}
                </Alert>
            )}

            <IfHasAllPermissions permissions={perms}>
                {errors?.map(err => (
                    <CampaignErrorAlert key={err.id} error={err} hideOutcome={hideOutcome} />
                ))}
            </IfHasAllPermissions>
        </Box>
    );
};

export default CreativeIssuesGrid;
